import { usePunterStore } from '@/stores/PunterStore.ts';
import { useStore } from '@/stores/store';

import { getItemFromStorage, setItemToStorage } from '@/services/storage';
import {
  getPunterFavourites,
  loadFavouritesOffer,
  updatePunterFavourites,
} from '@/api/distribution';
import { constructClientIds, constructEndDate, constructStartDate } from '@/utils/helpers';

export enum Entities {
  TOURNAMENTS = 'tournaments',
  EVENTS = 'events',
  MARKETS = 'markets',
}

enum EntitiesRemoval {
  tournaments = 'tournamentsForRemoval',
  events = 'eventsForRemoval',
  markets = 'marketsForRemoval',
}

// @ToDo: Move to /src/shared once /api/distribution.js is rewritten to TS
type Query = {
  clientIds?: {
    channel: string;
    punterId?: string;
  };
  expectedNumberOfEvents?: number;
  numberOfMarketsPerSport?: number;
  excludeSportCategoryTournamentMetadata?: boolean;
  includeEventCountStats?: boolean;
  tournaments?: number[];
  events?: number[];
  supplementalNameTypeIds?: number[];
  startsAtFrom?: string;
  startsAtTo?: string;
};

export class Favourites {
  static getTimeFilters() {
    const store = useStore();

    if (store.selectedFiltersExternal?.time === 'all') return {};

    const startDate =
      store.timeFilters[store.selectedFiltersExternal.time]?.date ?? constructStartDate();

    const endDate =
      store.timeFilters[store.selectedFiltersExternal.time]?.endDate ??
      constructEndDate(startDate) ??
      constructEndDate();

    return { startsAtFrom: startDate, startsAtTo: endDate };
  }

  static async loadNumberOfFavouriteEvents() {
    const { favourites } = usePunterStore();
    const store = useStore();

    const query: Query = {
      expectedNumberOfEvents: 0,
      numberOfMarketsPerSport: 0,
      excludeSportCategoryTournamentMetadata: true,
      includeEventCountStats: true,
      clientIds: constructClientIds(),
      ...Favourites.getTimeFilters(),
    };

    if (!window.punterToken) {
      query.tournaments = [...favourites.tournaments];
      query.events = [...favourites.events];
    }

    try {
      const response = await loadFavouritesOffer(query, store.isLive);
      store.setNumberOfFavouriteEvents(Object.values(response.data?.sportNumberOfEvents ?? {}));
    } catch (error) {
      console.log('An error occurred loading the number of favourite events. Error: ', error);
    }
  }

  static async loadOffer(cursor: string = '') {
    const { favourites } = usePunterStore();
    const store = useStore();

    const query: Query = {
      numberOfMarketsPerSport: 6,
      clientIds: constructClientIds(),
      includeEventCountStats: true,
      ...(store.config?.supplemental?.list && {
        supplementalNameTypeIds: store.config.supplemental.list,
      }),
      ...Favourites.getTimeFilters(),
    };

    if (!window.punterToken) {
      query.tournaments = [...favourites.tournaments];
      query.events = [...favourites.events];
    }

    try {
      const response = await loadFavouritesOffer(query, store.isLive, cursor);
      if (cursor) return response;

      store.useOfferAdapter('INITIAL_OFFER_SETUP', response.data);
      store.getTournamentGroupingConfigs(
        store.isLive,
        response.data.tournaments.map(({ id }: { id: number }) => id),
      );
      store.setNumberOfFavouriteEvents(Object.values(response.data?.sportNumberOfEvents ?? {}));
    } catch (error) {
      console.log('An error occurred loading the favourites offer. Error: ', error);
    }
  }

  static load() {
    const { selectedFiltersExternal } = useStore();

    if (selectedFiltersExternal.favourites) void Favourites.loadOffer();
    else void Favourites.loadNumberOfFavouriteEvents();
  }

  static async getFavourites() {
    const { favourites, clearFavourites } = usePunterStore();
    clearFavourites();

    const rawData = window.punterToken
      ? await getPunterFavourites()
      : getItemFromStorage('punter-favourites');

    Object.keys(rawData ?? {}).forEach((slug) => {
      favourites[slug] = new Set(rawData[slug]);
    });
  }

  static updateStorage(favourites: { [k: string]: Set<number> }) {
    setItemToStorage(
      'punter-favourites',
      Object.fromEntries(Object.entries(favourites).map(([key, value]) => [key, [...value]])),
    );
  }

  static async addFavourite(entity: Entities, id: number) {
    const { favourites } = usePunterStore();
    favourites[entity].add(id);

    if (window.punterToken) await updatePunterFavourites({ [entity]: [id] });
    else Favourites.updateStorage(favourites);
  }

  static async removeFavourite(entity: Entities, id: number) {
    const { favourites } = usePunterStore();
    favourites[entity].delete(id);

    if (window.punterToken) await updatePunterFavourites({ [EntitiesRemoval[entity]]: [id] });
    else Favourites.updateStorage(favourites);
  }
}
